import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Drawer from '@material-ui/core/Drawer';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import { isAuthenticated, sendLogin, changePassword } from '../../auth/Auth';
import { getAccounts, editAccountStatus } from '../../actions/Accounts';
import ValidationText from '../../components/atoms/ValidationText';
import RequiredButton from '../../components/atoms/RequiredButton';
import ChangeButton from '../../components/atoms/ChangeButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';

const styles = {
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: 1,
    padding: 10
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  button: {
    margin: '5px 5px'
  },
  buttonLeft: {
    textAlign: 'left'
    // CSV出力ボタンを実装時にコメントアウトを外す
    // float: 'left'
  },
  buttonGroupLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  },
  buttonGroupRight: {
    textAlign: 'right'
  },
  editTable: {
    width: 'auto'
  },
  tableCell90: {
    width: '90%'
  },
  tableCellBorderNone: {
    borderBottom: 'none'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account
    }),
    dispatch => ({
      actions: bindActionCreators({ getAccounts, editAccountStatus }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openChangeProgress', 'updateOpenChangeProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isChangeSuccess', 'updateIsChangeSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('redirectPath', 'setRedirectPath', '/shops'),
  withState('oldPassword', 'setOldPassword', ''),
  withState('password', 'setPassword', ''),
  withState('passwordError', 'setPasswordError', ''),
  withState('confirmPassword', 'setConfirmPassword', ''),
  withState('confirmPasswordError', 'setConfirmPasswordError', ''),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(data => {
        this.props.checkSession(data);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleRedirect,
    account,
    openDrawer,
    updateOpenDrawer,
    openChangeProgress,
    updateOpenChangeProgress,
    loading,
    updateLoading,
    isChangeSuccess,
    updateIsChangeSuccess,
    resultMessage,
    setResultMessage,
    redirectPath,
    setRedirectPath,
    oldPassword,
    setOldPassword,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    confirmPasswordError,
    setConfirmPasswordError
  }) => {
    const isReleased =
      account && account.accountStatus && account.accountStatus.status === true;

    const errors = {
      passwordError,
      confirmPasswordError
    };
    const validPassword = () => {
      const value = password;
      if (value === '') {
        setPasswordError('必須項目です。');
        return 1;
      }
      if (
        value.length < 8 ||
        !/[A-Z]/.test(value) ||
        !/[a-z]/.test(value) ||
        !/[0-9]/.test(value)
      ) {
        setPasswordError(
          '英大文字・英小文字・数字全てを含め、8文字以上で入力してください。'
        );
        return 1;
      }
      setPasswordError('');
      setPassword(value);
      return 0;
    };
    const validConfirmPassword = () => {
      const value = password;
      const valueConfirm = confirmPassword;
      if (valueConfirm === '') {
        setConfirmPasswordError('必須項目です。');
        return 1;
      }
      if (value !== valueConfirm) {
        setConfirmPasswordError('パスワードが一致していません。');
        return 1;
      }
      setConfirmPasswordError('');
      setConfirmPassword(value);
      return 0;
    };
    const valid = () => {
      const errorCount = validPassword() + validConfirmPassword();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const changeAction = async () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenChangeProgress(true);
        const accountData = await actions
          .getAccounts({ cognitoUserId: '' })
          .then(data => checkSession(data));
        if (account.role === 'GROUP') {
          setRedirectPath('/groups_group');
        } else if (isReleased !== null && !isReleased) {
          setRedirectPath('/release_step');
        }
        await changePassword(oldPassword, password)
          .then(async () => {
            const params = accountData.payload;
            params.status = 'AVAILABLE';
            const editedStatus = await actions.editAccountStatus(
              params.manageAccountId,
              params
            );
            if (editedStatus.type === 'EDIT_ACCOUNT_STATUS_SUCCESS') {
              updateIsChangeSuccess(true);
              setResultMessage('パスワードを変更しました。');
              updateLoading(false);
            } else {
              updateIsChangeSuccess(false);
              setResultMessage('パスワード変更に失敗しました。');
              updateLoading(false);
            }
          })
          .catch(() => {
            updateIsChangeSuccess(false);
            setResultMessage('パスワード変更に失敗しました。');
            updateLoading(false);
          });
      }
    };

    const progressOkAction = () => {
      if (isChangeSuccess) {
        updateOpenChangeProgress(false);
        handleRedirect(redirectPath);
      } else {
        updateOpenChangeProgress(false);
      }
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            パスワード変更
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <Table className={classes.editTable}>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone}>
                  <RequiredButton />
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCellBorderNone,
                    classes.tableCell90
                  )}
                >
                  <TextField
                    required
                    label="旧パスワード"
                    type="password"
                    fullWidth
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setOldPassword(e.target.value);
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone}>
                  <RequiredButton />
                </TableCell>
                <TableCell className={classes.tableCellBorderNone}>
                  <TextField
                    required
                    label="新パスワード"
                    type="password"
                    fullWidth
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setPassword(e.target.value);
                    }}
                    onBlur={validPassword}
                  />
                  <ValidationText message={errors.passwordError} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone} />
                <TableCell className={classes.tableCellBorderNone}>
                  パスワードは英大文字・英小文字・数字の３種類全てを含め、8文字以上で設定してください。
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone}>
                  <RequiredButton />
                </TableCell>
                <TableCell className={classes.tableCellBorderNone}>
                  <TextField
                    required
                    label="確認用パスワード"
                    type="password"
                    fullWidth
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setConfirmPassword(e.target.value);
                    }}
                    onBlur={validConfirmPassword}
                  />
                  <ValidationText message={errors.confirmPasswordError} />
                </TableCell>
              </TableRow>
            </Table>
          </div>
          <div>
            <ChangeButton onClickAction={changeAction} />
          </div>
          <ProgressDialog
            openProgress={openChangeProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
          />
        </Paper>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
